import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import OgMeta from '@/components/OgMeta';
import procedimentos from '@/data/procedimentos.json';

/**
 * Glossário de termos de cirurgia plástica.
 *
 * Existe para desambiguar o vocabulário do site — "mastopexia" e "mamoplastia"
 * não são sinônimos, "lipoenxertia" e "lipoaspiração" também não. Termo com
 * página própria linka para ela; os demais ficam só definidos.
 *
 * Nada aqui é indicação clínica: é dicionário, não conduta.
 *
 * REVISÃO: as 29 definições abaixo já estavam publicadas antes de 24/09/2026.
 * Termo NOVO: definição PENDENTE DE REVISÃO pelo Dr. Luis Brunello antes de
 * publicar — definição médica não vai ao ar sem o profissional.
 */
interface Term {
  term: string;
  definition: string;
  /** Rota da página do procedimento, quando existe. */
  to?: string;
  /** Slugs de src/data/procedimentos.json ligados ao termo ("Relacionado:"). */
  related: string[];
}

const SITE = 'https://luisbrunello.com.br';
const PROCEDIMENTOS = procedimentos.flatMap((g) => g.items);
const PROC_BY_SLUG = Object.fromEntries(PROCEDIMENTOS.map((p) => [p.slug, p]));

/** Âncora do termo: "Areolar (incisão)" → "areolar-incisao". */
const anchor = (term: string) =>
  term.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const TERMS: { letter: string; items: Term[] }[] = [
  {
    letter: 'A',
    items: [
      {
        term: 'Abdominoplastia',
        related: ['abdominoplastia', 'mini-abdominoplastia'],
        definition:
          'Cirurgia que remove o excesso de pele do abdômen e, quando necessário, reaproxima os músculos abdominais separados (diástase). Não é um método de emagrecimento: trata pele e parede abdominal, não gordura generalizada.',
        to: '/procedimentos/abdominoplastia',
      },
      {
        term: 'Anestesia peridural',
        related: ['abdominoplastia', 'lipoaspiracao'],
        definition:
          'Bloqueio anestésico aplicado na região da coluna que insensibiliza da cintura para baixo, mantendo o paciente consciente ou sedado. Uma das opções em cirurgias de abdômen e membros inferiores; a escolha é do anestesiologista, caso a caso.',
      },
      {
        term: 'Areolar (incisão)',
        related: ['mamoplastia-de-aumento', 'mastopexia-com-protese'],
        definition:
          'Corte feito no limite entre a aréola e a pele, onde a diferença de cor ajuda a disfarçar a cicatriz. Usado em algumas técnicas de mama, quando o volume a ser tratado permite.',
      },
    ],
  },
  {
    letter: 'B',
    items: [
      {
        term: 'Blefaroplastia',
        related: ['blefaroplastia'],
        definition:
          'Cirurgia das pálpebras. Remove excesso de pele e, quando indicado, as bolsas de gordura que dão aspecto de cansaço ao olhar. Pode ser superior, inferior ou ambas.',
        to: '/procedimentos/blefaroplastia',
      },
    ],
  },
  {
    letter: 'C',
    items: [
      {
        term: 'Cânula',
        related: ['lipoaspiracao', 'lipoenxertia-na-face'],
        definition:
          'Tubo fino e rombo usado na lipoaspiração e na lipoenxertia para aspirar ou injetar gordura. O diâmetro varia conforme a área e a finalidade — cânulas finas para a face, mais calibrosas para o contorno corporal.',
      },
      {
        term: 'Contratura capsular',
        related: ['troca-de-protese-mamaria', 'mamoplastia-de-aumento'],
        definition:
          'Endurecimento da cápsula de tecido que o corpo forma naturalmente ao redor de uma prótese mamária. Em grau leve não incomoda; em grau avançado altera forma e causa desconforto, e pode exigir nova cirurgia.',
        to: '/procedimentos/troca-de-protese-mamaria',
      },
      {
        term: 'Cirurgias combinadas',
        related: ['cirurgias-combinadas', 'mommy-makeover'],
        definition:
          'Dois ou mais procedimentos realizados no mesmo tempo cirúrgico. Depende de tempo total de anestesia, condição clínica e compatibilidade entre as técnicas — nem toda combinação é segura.',
        to: '/procedimentos/cirurgias-combinadas',
      },
    ],
  },
  {
    letter: 'D',
    items: [
      {
        term: 'Diástase abdominal',
        related: ['abdominoplastia', 'mini-abdominoplastia'],
        definition:
          'Afastamento dos músculos retos do abdômen, comum após gestação. Produz a projeção abdominal que não cede com exercício, porque o problema é a separação muscular e não a gordura. É corrigida durante a abdominoplastia.',
      },
      {
        term: 'Dreno',
        related: ['abdominoplastia', 'mamoplastia-redutora'],
        definition:
          'Tubo fino deixado temporariamente na área operada para escoar líquido acumulado. Nem toda cirurgia usa; quando usado, costuma ser retirado nos primeiros dias de pós-operatório.',
      },
    ],
  },
  {
    letter: 'E',
    items: [
      {
        term: 'Enxertia de gordura',
        related: ['enxertia-de-gordura-na-mama', 'lipoaspiracao-com-enxertia-glutea', 'lipoenxertia-na-face'],
        definition:
          'Transferência de gordura do próprio paciente, aspirada de uma área e injetada em outra depois de processada. Parte do volume é reabsorvida pelo corpo nos primeiros meses, o que já entra no planejamento.',
        to: '/procedimentos/enxertia-de-gordura-na-mama',
      },
    ],
  },
  {
    letter: 'L',
    items: [
      {
        term: 'Lipoaspiração',
        related: ['lipoaspiracao', 'lipoaspiracao-com-enxertia-glutea'],
        definition:
          'Remoção de gordura localizada por aspiração, para redesenhar o contorno. Trata depósitos que resistem a dieta e exercício; não substitui perda de peso nem trata flacidez de pele.',
        to: '/procedimentos/lipoaspiracao',
      },
      {
        term: 'Lipoenxertia',
        related: ['lipoenxertia-na-face', 'enxertia-de-gordura-na-mama'],
        definition:
          'A lipoaspiração e a enxertia no mesmo ato: a gordura retirada de uma região é reinjetada em outra. Na face, devolve volume perdido; nos glúteos, projeta e arredonda.',
        to: '/procedimentos/lipoenxertia-na-face',
      },
      {
        term: 'Lifting corporal',
        related: ['lifting-corporal-pos-perda-de-peso'],
        definition:
          'Cirurgia que remove o excesso de pele que sobra após grande perda de peso, geralmente em abdômen, flancos e dorso, em um único contorno circunferencial. Indicada com o peso já estabilizado.',
        to: '/procedimentos/lifting-corporal-pos-perda-de-peso',
      },
    ],
  },
  {
    letter: 'M',
    items: [
      {
        term: 'Mamoplastia de aumento',
        related: ['mamoplastia-de-aumento'],
        definition:
          'Aumento do volume das mamas com prótese de silicone. Define volume, projeção e formato; não corrige, sozinha, mama caída — nesse caso associa-se a mastopexia.',
        to: '/procedimentos/mamoplastia-de-aumento',
      },
      {
        term: 'Mamoplastia redutora',
        related: ['mamoplastia-redutora'],
        definition:
          'Redução do volume das mamas com remodelagem, aliviando queixas de dor cervical, sulcos nas alças e dermatite no sulco mamário. Reposiciona a aréola no mesmo tempo cirúrgico.',
        to: '/procedimentos/mamoplastia-redutora',
      },
      {
        term: 'Mastopexia',
        related: ['mastopexia-sem-protese', 'mastopexia-com-protese'],
        definition:
          'Levantamento da mama: reposiciona o tecido e a aréola sem necessariamente mudar o volume. Com prótese quando falta volume no polo superior, sem prótese quando o volume já é suficiente. Não é sinônimo de mamoplastia de aumento.',
        to: '/procedimentos/mastopexia-sem-protese',
      },
      {
        term: 'Mommy makeover',
        related: ['mommy-makeover'],
        definition:
          'Nome informal para a combinação de procedimentos que tratam as alterações corporais após gestação e amamentação — em geral mama e abdômen no mesmo tempo cirúrgico. A composição muda conforme o caso.',
        to: '/procedimentos/mommy-makeover',
      },
      {
        term: 'Malha compressiva',
        related: ['lipoaspiracao', 'abdominoplastia'],
        definition:
          'Peça elástica usada no pós-operatório para reduzir edema, acomodar os tecidos e dar conforto. O tempo de uso varia por procedimento e é definido na alta.',
      },
    ],
  },
  {
    letter: 'O',
    items: [
      {
        term: 'Otoplastia',
        related: ['otoplastia'],
        definition:
          'Cirurgia que corrige a posição e o formato das orelhas proeminentes, remodelando a cartilagem. Pode ser feita a partir da idade escolar, quando a orelha já atingiu quase todo o crescimento.',
        to: '/procedimentos/otoplastia',
      },
    ],
  },
  {
    letter: 'P',
    items: [
      {
        term: 'Perfil da prótese',
        related: ['mamoplastia-de-aumento', 'troca-de-protese-mamaria'],
        definition:
          'Relação entre a projeção e a base da prótese. Perfil alto projeta mais sobre uma base estreita; perfil moderado distribui o volume de forma mais larga. A escolha depende da medida do tórax, não só do volume desejado.',
      },
      {
        term: 'Plano de colocação',
        related: ['mamoplastia-de-aumento', 'mastopexia-com-protese'],
        definition:
          'Onde a prótese é posicionada: acima do músculo peitoral (subglandular), abaixo dele (submuscular) ou parcialmente (dual plane). A decisão considera a espessura do tecido da paciente.',
      },
      {
        term: 'Preenchimento facial',
        related: ['preenchimento-facial'],
        definition:
          'Procedimento não cirúrgico que repõe volume com ácido hialurônico em áreas como sulcos, lábios e contorno. Tem efeito temporário e é reversível.',
        to: '/procedimentos/preenchimento-facial',
      },
      {
        term: 'Ptose mamária',
        related: ['mastopexia-sem-protese', 'mastopexia-com-protese'],
        definition:
          'Termo clínico para mama caída: a aréola fica abaixo do sulco mamário. O grau de ptose é o que define se o caso pede mastopexia, prótese ou os dois.',
      },
    ],
  },
  {
    letter: 'R',
    items: [
      {
        term: 'RQE',
        related: [],
        definition:
          'Registro de Qualificação de Especialista, número emitido pelo Conselho Regional de Medicina que comprova a especialidade registrada. O do Dr. Luis Brunello em Cirurgia Plástica é RQE 38.853.',
        to: '/sobre',
      },
      {
        term: 'Retorno (consulta de)',
        related: [],
        definition:
          'Consultas de acompanhamento no pós-operatório, em intervalos definidos pelo cirurgião. Fazem parte do tratamento: é nelas que a cicatrização é avaliada e os cuidados ajustados.',
        to: '/jornada',
      },
    ],
  },
  {
    letter: 'S',
    items: [
      {
        term: 'Seroma',
        related: ['abdominoplastia', 'lipoaspiracao'],
        definition:
          'Acúmulo de líquido claro no espaço operado, mais comum em áreas amplas como o abdômen. Quando ocorre, costuma ser resolvido no consultório por aspiração.',
      },
      {
        term: 'Sulco inframamário',
        related: ['mamoplastia-de-aumento', 'mamoplastia-redutora'],
        definition:
          'A dobra na base da mama. Serve de referência para medir ptose e é uma das vias de incisão em cirurgia mamária, por esconder bem a cicatriz.',
      },
    ],
  },
  {
    letter: 'T',
    items: [
      {
        term: 'Toxina botulínica',
        related: ['toxina-botulinica'],
        definition:
          'Substância aplicada para relaxar temporariamente músculos responsáveis por rugas de expressão. Efeito de meses, não permanente.',
        to: '/procedimentos/toxina-botulinica',
      },
      {
        term: 'Troca de prótese',
        related: ['troca-de-protese-mamaria'],
        definition:
          'Substituição de implantes já existentes, por contratura capsular, mudança de volume desejado, alteração de forma ao longo do tempo ou tempo de uso. Prótese não tem prazo fixo de validade, mas tem acompanhamento.',
        to: '/procedimentos/troca-de-protese-mamaria',
      },
    ],
  },
];

/**
 * DefinedTermSet: a forma que o schema.org tem para dizer "isto e um glossario".
 * Derivado de TERMS, entao nao ha lista paralela para sair de sincronia.
 */
const GLOSSARY_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'DefinedTermSet',
  '@id': 'https://luisbrunello.com.br/glossario#glossario',
  name: 'Glossário de cirurgia plástica',
  description:
    'Termos de cirurgia plástica usados no site do Dr. Luis Brunello, definidos em linguagem direta.',
  url: 'https://luisbrunello.com.br/glossario',
  inLanguage: 'pt-BR',
  hasDefinedTerm: TERMS.flatMap(({ items }) =>
    items.map(({ term, definition }) => ({
      '@type': 'DefinedTerm',
      '@id': `${SITE}/glossario#${anchor(term)}`,
      url: `${SITE}/glossario#${anchor(term)}`,
      name: term,
      description: definition,
      inDefinedTermSet: 'https://luisbrunello.com.br/glossario#glossario',
    })),
  ),
};

const GlossarioPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { hash } = useLocation();

  // Link direto para /glossario#termo: a rota é lazy, então quando o navegador
  // tenta rolar até a âncora o termo ainda não existe. Rola depois de montar.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="min-h-screen bg-dark-base">
      <Helmet>
        <title>Glossário de Cirurgia Plástica | Dr. Luis Brunello</title>
        <meta
          name="description"
          content="Glossário de termos de cirurgia plástica: mastopexia, lipoenxertia, diástase, ptose, contratura capsular e outros. Definições diretas, sem jargão."
        />
        <link rel="canonical" href="https://luisbrunello.com.br/glossario" />
        <script type="application/ld+json">{JSON.stringify(GLOSSARY_SCHEMA)}</script>
      </Helmet>
      <OgMeta
        title="Glossário de Cirurgia Plástica | Dr. Luis Brunello"
        description="Glossário de termos de cirurgia plástica: mastopexia, lipoenxertia, diástase, ptose, contratura capsular e outros. Definições diretas, sem jargão."
        path="/glossario"
      />

      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrollToSection={() => {}} onOpenBooking={() => {}} />

      <main className="pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">

          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-light-muted mb-10">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <span aria-hidden="true">/</span>
            <span className="text-light-secondary" aria-current="page">Glossário</span>
          </nav>

          <header className="mb-12">
            <p className="subtitle-wide text-gold mb-3 block">Terminologia</p>
            <h1 className="text-3xl md:text-4xl text-light-primary tracking-tight leading-tight mb-4">
              Glossário de cirurgia plástica
            </h1>
            <p className="text-light-muted text-sm leading-relaxed">
              Os termos que aparecem nas páginas deste site, definidos em linguagem direta. Vários são
              confundidos entre si — mastopexia não é mamoplastia de aumento, lipoenxertia não é
              lipoaspiração — e a diferença muda o que a cirurgia faz. Nenhuma definição aqui é indicação
              clínica: qual procedimento cabe no seu caso só se define em{' '}
              <Link to="/contato" className="text-gold hover:underline">consulta presencial</Link>.
            </p>
          </header>

          <nav aria-label="Índice alfabético" className="flex flex-wrap gap-2 mb-12 pb-8 border-b border-white/5">
            {TERMS.map(({ letter }) => (
              <a
                key={letter}
                href={`#letra-${letter}`}
                className="w-8 h-8 flex items-center justify-center text-xs text-light-muted border border-white/10 rounded hover:text-gold hover:border-gold transition-colors"
              >
                {letter}
              </a>
            ))}
          </nav>

          {TERMS.map(({ letter, items }) => (
            <section key={letter} aria-labelledby={`letra-${letter}`} className="mb-12">
              <h2
                id={`letra-${letter}`}
                className="text-xl text-gold font-medium mb-6 pb-2 border-b border-white/5 scroll-mt-28"
              >
                {letter}
              </h2>
              <dl className="space-y-6">
                {items.map(({ term, definition, to, related }) => (
                  <div key={term} id={anchor(term)} className="scroll-mt-28">
                    <dt className="text-light-primary font-medium text-sm mb-1">
                      {to ? (
                        <Link to={to} className="hover:text-gold transition-colors">{term}</Link>
                      ) : (
                        term
                      )}
                    </dt>
                    <dd className="text-light-muted text-sm leading-relaxed">{definition}</dd>
                    {related.length > 0 && (
                      <dd className="text-light-muted text-xs mt-1">
                        Relacionado:{' '}
                        {related.map((slug, i) => (
                          <React.Fragment key={slug}>
                            {i > 0 && ', '}
                            <Link to={`/procedimentos/${slug}`} className="text-gold hover:underline">
                              {PROC_BY_SLUG[slug].name}
                            </Link>
                          </React.Fragment>
                        ))}
                      </dd>
                    )}
                  </div>
                ))}
              </dl>
            </section>
          ))}

          {/* Descrição = texto aprovado dos cards da home (procedimentos.json). */}
          <section aria-labelledby="procedimentos" className="mb-12">
            <h2 id="procedimentos" className="text-xl text-gold font-medium mb-6 pb-2 border-b border-white/5 scroll-mt-28">
              Procedimentos
            </h2>
            <dl className="space-y-6">
              {PROCEDIMENTOS.map(({ slug, name, description }) => (
                <div key={slug}>
                  <dt className="text-light-primary font-medium text-sm mb-1">
                    <Link to={`/procedimentos/${slug}`} className="hover:text-gold transition-colors">{name}</Link>
                  </dt>
                  <dd className="text-light-muted text-sm leading-relaxed">{description}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div className="pt-8 border-t border-white/5 text-sm text-light-muted leading-relaxed">
            <p>
              Não encontrou um termo? A{' '}
              <Link to="/jornada" className="text-gold hover:underline">jornada do paciente</Link>{' '}
              explica o processo do início ao fim, e a equipe responde dúvidas pelo{' '}
              <Link to="/contato" className="text-gold hover:underline">canal de contato</Link>.
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GlossarioPage;
